import { useState, useCallback, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  Select,
  Group,
  Badge,
  ActionIcon,
  Menu,
  Text,
  TextInput,
  Paper,
  Stack,
  Skeleton,
  Textarea,
  Pagination,
  Code,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconTrash,
} from '@tabler/icons-react';
import { activationsService } from '../../services/api/activations';
import { customersService } from '../../services/api/customers';
import { useActionMutation, useFormModal, useTableSort, showValidationError } from '../../hooks';
import { useDisclosure } from '@mantine/hooks';
import { SearchToolbar, FormModal, SortableHeader, DetailModal, DetailField } from '../../components/shared';
import { LICENSE_STATUS_COLORS, PLATFORM_LABELS } from '../../constants';
import type { ActivationWithLicense, Platform } from '../../types';
import dayjs from 'dayjs';

interface DeactivateFormData {
  reason: string;
}

export function ActivationsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [platformFilter, setPlatformFilter] = useState<string | null>(null);
  const [appVersionFilter, setAppVersionFilter] = useState('');
  const [debouncedAppVersion] = useDebouncedValue(appVersionFilter, 300);
  const [customerFilter, setCustomerFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [viewItem, setViewItem] = useState<ActivationWithLicense | null>(null);

  const deactivateModal = useFormModal<DeactivateFormData, ActivationWithLicense>({
    initialData: { reason: '' },
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: () => customersService.list({ limit: 1000 }),
  });

  const customerOptions = useMemo(() =>
    (customersData?.customers || []).map(c => ({ value: c.id, label: c.name })),
    [customersData]
  );

  const { data: activationsData, isLoading } = useQuery({
    queryKey: ['activations', debouncedSearch, platformFilter, debouncedAppVersion, customerFilter],
    queryFn: () =>
      activationsService.list({
        eid: debouncedSearch || undefined,
        platform: (platformFilter as Platform) || undefined,
        app_version: debouncedAppVersion || undefined,
        customer_id: customerFilter || undefined,
        limit: 100,
      }),
  });

  const deactivateMutation = useActionMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      activationsService.deactivate(id, reason),
    {
      queryKeys: ['activations'],
      successMessage: 'Activation deactivated successfully',
      onSuccessCallback: deactivateModal.close,
    }
  );

  const handleDeactivate = () => {
    if (!deactivateModal.selectedItem || !deactivateModal.formData.reason?.trim()) {
      showValidationError('Please provide a reason for deactivation');
      return;
    }
    deactivateMutation.mutate({
      id: deactivateModal.selectedItem.id,
      reason: deactivateModal.formData.reason,
    });
  };

  const resetPage = useCallback(() => setPage(1), []);
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort<ActivationWithLicense>(resetPage);

  const activations = activationsData?.activations || [];

  const sortAccessors: Record<string, (item: ActivationWithLicense) => unknown> = useMemo(() => ({
    eid: (a) => a.eid,
    platform: (a) => a.platform,
    customer: (a) => a.customerName,
    appVersion: (a) => a.appVersion,
    activated: (a) => a.activatedAt,
    last_verified: (a) => a.lastValidatedAt,
    transfer: (a) => !!a.transferredFromActivationId,
  }), []);

  const sortedActivations = sortData(activations, sortAccessors);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedActivations.length / PAGE_SIZE);
  const paginatedActivations = sortedActivations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Activations</Title>
      </Group>

      <SearchToolbar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        placeholder="Search by EID..."
      >
        <Select
          placeholder="Platform"
          clearable
          value={platformFilter}
          onChange={(v) => { setPlatformFilter(v); setPage(1); }}
          data={[
            { value: 'ios', label: 'iOS' },
            { value: 'windows', label: 'Windows' },
            { value: 'android', label: 'Android' },
          ]}
          w={150}
        />
        <TextInput
          placeholder="App Version"
          value={appVersionFilter}
          onChange={e => { setAppVersionFilter(e.target.value); setPage(1); }}
          w={150}
        />
        <Select
          placeholder="Customer"
          clearable
          searchable
          value={customerFilter}
          onChange={(v) => { setCustomerFilter(v); setPage(1); }}
          data={customerOptions}
          w={200}
        />
      </SearchToolbar>

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : activations.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No activations found
          </Text>
        ) : (
          <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableHeader column="eid" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>EID</SortableHeader>
                <SortableHeader column="platform" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Platform</SortableHeader>
                <SortableHeader column="customer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Customer</SortableHeader>
                <SortableHeader column="appVersion" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>App Version</SortableHeader>
                <SortableHeader column="activated" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Activated</SortableHeader>
                <SortableHeader column="last_verified" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Last Verified</SortableHeader>
                <SortableHeader column="transfer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Transfer</SortableHeader>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedActivations.map(activation => (
                <Table.Tr key={activation.id} onClick={() => { setViewItem(activation); openViewModal(); }} style={{ cursor: 'pointer' }}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {activation.eid}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{PLATFORM_LABELS[activation.platform]}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{activation.customerName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {activation.appVersion || '—'}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    <Text size="sm">
                      {dayjs(activation.activatedAt).format('MMM D, YYYY h:mm A')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {activation.lastValidatedAt
                        ? dayjs(activation.lastValidatedAt).format('MMM D, YYYY')
                        : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {activation.transferredFromActivationId ? (
                      <Badge color="violet" variant="light" size="sm">
                        Transferred
                      </Badge>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {activation.status === 'active' && (
                      <Menu position="bottom-end" withArrow>
                        <Menu.Target>
                          <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => deactivateModal.openForEdit(activation, { reason: '' })}
                          >
                            Deactivate
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedActivations.length)}–{Math.min(page * PAGE_SIZE, sortedActivations.length)} of {sortedActivations.length}
            </Text>
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            )}
          </Group>
          </>
        )}
      </Paper>

      {/* View Activation Modal */}
      {viewItem && (
        <DetailModal
          opened={viewModalOpened}
          onClose={closeViewModal}
          title="Activation Details"
          recordId={viewItem.id}
          size="xl"
          actions={[
            {
              label: 'Deactivate',
              icon: <IconTrash size={16} />,
              color: 'red',
              onClick: () => { closeViewModal(); deactivateModal.openForEdit(viewItem, { reason: '' }); },
              hidden: viewItem.status !== 'active',
            },
          ]}
        >
          <DetailField label="EID" value={viewItem.eid} />
          <DetailField label="Status" value={
            <Badge color={viewItem.status === 'active' ? 'green' : 'gray'} variant="light">{viewItem.status === 'active' ? 'Active' : 'Deactivated'}</Badge>
          } />
          <DetailField label="Platform" value={PLATFORM_LABELS[viewItem.platform]} />
          <DetailField label="App Version" value={viewItem.appVersion} />
          <DetailField label="Customer" value={viewItem.customerName} />
          <DetailField label="Customer Email" value={viewItem.customerEmail} />
          <DetailField label="Product" value={
            <Group gap="xs" wrap="nowrap">
              <Text size="sm">{viewItem.productName}</Text>
              {viewItem.productIsDemo && (
                <Badge color="violet" variant="light" size="xs">Demo</Badge>
              )}
            </Group>
          } />
          <DetailField label="Trial" value={
            <Badge color="gray" variant="light">{viewItem.isTrial ? 'Yes' : 'No'}</Badge>
          } />
          <DetailField label="License ID" value={<Code>{viewItem.licenseId}</Code>} />
          <DetailField label="License Status" value={
            <Badge color={LICENSE_STATUS_COLORS[viewItem.licenseStatus as keyof typeof LICENSE_STATUS_COLORS] || 'gray'} variant="light">{viewItem.licenseStatus}</Badge>
          } />
          <DetailField label="License Term End" value={viewItem.licenseTermEnd ? dayjs(viewItem.licenseTermEnd).format('MMM D, YYYY') : undefined} />
          <DetailField label="Activation Token" value={<Code>{viewItem.activationToken}</Code>} />
          <DetailField label="Activated At" value={dayjs(viewItem.activatedAt).format('MMM D, YYYY h:mm A')} />
          <DetailField label="Last Verified" value={viewItem.lastValidatedAt ? dayjs(viewItem.lastValidatedAt).format('MMM D, YYYY h:mm A') : undefined} />
          <DetailField label="Validation Count" value={String(viewItem.validationCount)} />
          <DetailField label="Transfer" value={
            viewItem.transferredFromActivationId
              ? <Badge color="violet" variant="light">Transferred</Badge>
              : <Badge color="gray" variant="light">Original</Badge>
          } />
          <DetailField label="Transfer Confirmed" value={viewItem.transferConfirmedAt ? dayjs(viewItem.transferConfirmedAt).format('MMM D, YYYY h:mm A') : undefined} />
          <DetailField label="IP Address" value={viewItem.ipAddress} />
          <DetailField label="User Agent" value={viewItem.userAgent} multiline />
          {viewItem.deactivatedAt && (
            <DetailField label="Deactivated At" value={dayjs(viewItem.deactivatedAt).format('MMM D, YYYY h:mm A')} />
          )}
          {viewItem.deactivationReason && (
            <DetailField label="Deactivation Reason" value={viewItem.deactivationReason} multiline />
          )}
        </DetailModal>
      )}

      {/* Deactivate Modal */}
      <FormModal
        opened={deactivateModal.isOpen}
        onClose={deactivateModal.close}
        title="Deactivate Activation"
        isLoading={deactivateMutation.isPending}
        onSubmit={handleDeactivate}
        submitLabel="Deactivate"
        submitColor="red"
      >
        <Text size="sm">
          Are you sure you want to deactivate this activation? This will allow the license to be
          activated on another device.
        </Text>
        {deactivateModal.selectedItem && (
          <Paper withBorder p="sm" bg="gray.0">
            <Group>
              <Text size="sm">{PLATFORM_LABELS[deactivateModal.selectedItem.platform]}</Text>
              <Text size="sm" fw={500}>
                {deactivateModal.selectedItem.eid}
              </Text>
            </Group>
          </Paper>
        )}
        <Textarea
          label="Reason"
          placeholder="Enter reason for deactivation"
          required
          value={deactivateModal.formData.reason || ''}
          onChange={e => deactivateModal.setFormData({ ...deactivateModal.formData, reason: e.target.value })}
          minRows={3}
        />
      </FormModal>
    </Container>
  );
}
